import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckIcon, LockIcon, Share2Icon } from 'lucide-react';
import { BrandMark } from '../../components/BrandMark';
import { ShareSheet } from '../../components/engagement/ShareSheet';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card, SectionTitle } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Switch } from '../../components/ui/Switch';
import { progressPhotos } from '../../data/engagement';
import { useLocale } from '../../contexts/LocaleContext';
type Frame = 'split' | 'side' | 'vertical' | 'deep';
const frames: Frame[] = ['split', 'side', 'vertical', 'deep'];

/**
 * MOB/Profile/BeforeAfter — optional comparison frame for two progress photos.
 * Private by default; saving never publishes; sharing is a separate confirmed action.
 * Health metrics are never added to the frame.
 */
export function BeforeAfter() {
  const {
    t,
    locale
  } = useLocale();
  const dateLocale = locale === 'pt-BR' ? ptBR : undefined;
  const oldest = progressPhotos[progressPhotos.length - 1];
  const newest = progressPhotos[0];
  const [beforeId, setBeforeId] = useState(oldest.id);
  const [afterId, setAfterId] = useState(newest.id);
  const [beforeDate, setBeforeDate] = useState(oldest.date);
  const [afterDate, setAfterDate] = useState(newest.date);
  const [frame, setFrame] = useState<Frame>('split');
  const [showDates, setShowDates] = useState(true);
  const [showBrand, setShowBrand] = useState(true);
  const [saved, setSaved] = useState(false);
  const [confirmShare, setConfirmShare] = useState(false);
  const [sharing, setSharing] = useState(false);
  const before = progressPhotos.find(photo => photo.id === beforeId) ?? oldest;
  const after = progressPhotos.find(photo => photo.id === afterId) ?? newest;
  const fmt = (iso: string) => format(parseISO(iso), 'dd MMM yyyy', {
    locale: dateLocale
  });
  const error = beforeId === afterId ? t('ba.error.same') : afterDate <= beforeDate ? t('ba.error.order') : null;
  const dirty = () => setSaved(false);
  const pickPhoto = (side: 'before' | 'after', id: string) => {
    const photo = progressPhotos.find(item => item.id === id);
    if (!photo) return;
    if (side === 'before') {
      setBeforeId(id);
      setBeforeDate(photo.date);
    } else {
      setAfterId(id);
      setAfterDate(photo.date);
    }
    dirty();
  };
  const panes = [{
    key: 'before',
    url: before.url,
    label: t('ba.before'),
    date: beforeDate
  }, {
    key: 'after',
    url: after.url,
    label: t('ba.after'),
    date: afterDate
  }];
  const caption = (label: string, date: string, dark: boolean) => <span className={`absolute bottom-1 left-1 rounded-pill px-[8px] py-[3px] ${dark ? 'bg-deep text-gold' : 'bg-sand/95 text-deep'}`} data-id="element-2722" data-mp="2zftxmvv4wjjt">
      <span className="bee-overline text-[8.5px] font-bold" data-id="element-2723" data-mp="3rxxxolp6ou2n">{label}</span>
      {showDates && <span className="bee-num ml-1 text-[10.5px]" data-id="element-2724" data-mp="56lp24p31dr3">{fmt(date)}</span>}
    </span>;
  const isDeep = frame === 'deep';
  const layout = frame === 'vertical' ? 'grid-rows-2' : frame === 'split' ? 'grid-cols-2 gap-0' : 'grid-cols-2';
  return <div className="px-2 pb-2" data-id="element-2725" data-mp="ew59hesg1fo2">
      <PageHeader title={t('ba.title')} subtitle={t('ba.subtitle')} showBack action={<span className="bee-overline flex items-center gap-1 rounded-pill border border-sand-line px-2 py-[5px] text-[8.5px] font-bold text-deep" data-id="element-2727" data-mp="3id3y826txs8v">
            <LockIcon size={10} aria-hidden="true" data-id="element-2728" data-mp="3edv7l6736ahj" />
            {t('eng.photos.private')}
          </span>} data-id="element-2726" data-mp="9xty2zmi2x3q" />

      <p className="mb-2 text-[12px] leading-snug text-muted" data-id="element-2729" data-mp="3ncq9w770f0fk">{t('ba.privacy')}</p>

      {/* Preview */}
      <section aria-label={t('ba.preview')} data-id="element-2730" data-mp="17lm14a88osj8">
        <div className={`relative overflow-hidden rounded-card ${isDeep ? 'border border-gold/60 bg-deep p-2' : frame === 'split' ? 'bg-sand-deep' : 'bg-sand-deep p-1'}`} data-id="element-2731" data-mp="17kasnx3umwls">
          <div className={`grid ${layout} ${frame === 'split' ? '' : 'gap-1'}`} data-id="element-2732" data-mp="38cmgv4p1dwsy">
            {panes.map(pane => <figure key={pane.key} className={`relative overflow-hidden ${frame === 'split' ? '' : 'rounded-[12px]'} ${frame === 'vertical' ? 'aspect-[4/3]' : 'aspect-[3/4]'}`} data-id="element-2733" data-mp="3dbpmorcuc71z">
                <img src={pane.url} alt={t('ba.alt', {
              label: pane.label,
              date: fmt(pane.date)
            })} className="h-full w-full object-cover" data-id="element-2734" data-mp="3r8um48i1u7o9" />
                {caption(pane.label, pane.date, isDeep)}
              </figure>)}
          </div>
          {frame === 'split' && <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gold" aria-hidden="true" data-id="element-2735" data-mp="dv12rdaigeug" />}
          {showBrand && <span className={`absolute right-1 top-1 flex items-center gap-1 rounded-pill px-[6px] py-[3px] ${isDeep ? 'bg-deep' : 'bg-sand/90'}`} data-id="element-2736" data-mp="iudotvtk5hvz">
              <BrandMark size={14} data-id="element-2737" data-mp="1ybgzfn5364py" />
              <span className={`bee-overline text-[8px] font-bold ${isDeep ? 'text-sand' : 'text-deep'}`} data-id="element-2738" data-mp="1ub6ri4xqh48g">
                BEELYV
              </span>
            </span>}
        </div>
      </section>

      {/* Photos and dates */}
      <section className="mt-3" data-id="element-2739" data-mp="2m12xu8dmwfww">
        <SectionTitle data-id="element-2740" data-mp="2m4gzfeeh4l3a">{t('ba.title')}</SectionTitle>
        <Card className="space-y-2 p-2" data-id="element-2741" data-mp="2i0j4lfw6lwg1">
          {(['before', 'after'] as const).map(side => <div key={side} className="grid grid-cols-2 gap-2" data-id="element-2742" data-mp="2d90zqgr1fbps">
              <label className="block min-w-0" data-id="element-2743" data-mp="28azhfy1frgfd">
                <span className="text-[11.5px] font-medium text-deep" data-id="element-2744" data-mp="28a6k1noda39w">
                  {side === 'before' ? t('ba.pickBefore') : t('ba.pickAfter')}
                </span>
                <select value={side === 'before' ? beforeId : afterId} onChange={event => pickPhoto(side, event.target.value)} className="mt-1 h-11 w-full rounded-[12px] border border-sand-line bg-white px-2 text-[13px] text-deep" data-id="element-2745" data-mp="30zdgmh49gisi">
                  {progressPhotos.map(photo => <option key={photo.id} value={photo.id} data-id="element-2746" data-mp="3b1r7mtsbqct6">
                      {t('ba.photoOption', {
                  date: fmt(photo.date)
                })}
                    </option>)}
                </select>
              </label>
              <label className="block min-w-0" data-id="element-2747" data-mp="3647fcpysjzi8">
                <span className="text-[11.5px] font-medium text-deep" data-id="element-2748" data-mp="36621u8ppnzof">
                  {side === 'before' ? t('ba.beforeDate') : t('ba.afterDate')}
                </span>
                <input type="date" value={side === 'before' ? beforeDate : afterDate} onChange={event => {
              if (side === 'before') setBeforeDate(event.target.value);else setAfterDate(event.target.value);
              dirty();
            }} aria-invalid={error !== null} className="mt-1 h-11 w-full rounded-[12px] border border-sand-line bg-white px-2 text-[13px] text-deep" data-id="element-2749" data-mp="2w7clwlhdkpat" />
              </label>
            </div>)}
          {error && <p role="alert" className="text-[12px] font-medium text-deep" data-id="element-2750" data-mp="2jva7e39hng95">
              {error}
            </p>}
        </Card>
      </section>

      {/* Frame */}
      <section className="mt-3" data-id="element-2751" data-mp="2tmemd2z5z13z">
        <SectionTitle data-id="element-2752" data-mp="2tld8kqqrk0ik">{t('ba.frame')}</SectionTitle>
        <div className="grid grid-cols-2 gap-2" data-id="element-2753" data-mp="37le8o3z7f3wz">
          {frames.map(id => <button key={id} type="button" aria-pressed={frame === id} onClick={() => {
          setFrame(id);
          dirty();
        }} className={`min-h-[44px] rounded-card border px-2 text-[12.5px] font-medium transition-colors duration-150 ease-brand ${frame === id ? 'border-gold bg-gold/15 text-deep' : 'border-sand-line bg-white/70 text-muted hover:text-deep'}`} data-id="element-2754" data-mp="32mjtdc0g3xhm">
              {t(`ba.frame.${id}`)}
            </button>)}
        </div>

        <Card className="mt-2 divide-y divide-sand-line px-2" data-id="element-2755" data-mp="w7ilqfsajnb4">
          <div className="flex items-center justify-between gap-2 py-2" data-id="element-2756" data-mp="112xzz8u7nl82">
            <span className="text-[13px] text-deep" data-id="element-2757" data-mp="4erygusl58y0">{t('ba.showDates')}</span>
            <Switch checked={showDates} onChange={next => {
            setShowDates(next);
            dirty();
          }} label={t('ba.showDates')} data-id="element-2758" data-mp="8fbv7x98yajm" />
          </div>
          <div className="flex items-center justify-between gap-2 py-2" data-id="element-2759" data-mp="ddn342fzeaz7">
            <span className="text-[13px] text-deep" data-id="element-2760" data-mp="ieauwqc1avuh">{t('ba.showBrand')}</span>
            <Switch checked={showBrand} onChange={next => {
            setShowBrand(next);
            dirty();
          }} label={t('ba.showBrand')} data-id="element-2761" data-mp="1otty0np9fcnr" />
          </div>
          <div className="py-2" data-id="element-2762" data-mp="1ts54cogcj518">
            <div className="flex items-center justify-between gap-2" data-id="element-2763" data-mp="1tr3kbuhcr87d">
              <span className="text-[13px] text-muted" data-id="element-2764" data-mp="3e9xlt4wetonm">{t('ba.health')}</span>
              <span className="bee-overline rounded-pill border border-sand-line px-2 py-[3px] text-[8.5px] font-bold text-muted" data-id="element-2765" data-mp="39bckt39c30y4">
                {t('ba.soon')}
              </span>
            </div>
            <p className="mt-1 text-[11px] leading-snug text-muted" data-id="element-2766" data-mp="2zmmc78jsgeq5">{t('ba.healthNote')}</p>
          </div>
        </Card>
      </section>

      <div className="mt-3 space-y-2" data-id="element-2767" data-mp="2vm2ea2re572y">
        <Button variant="primary" size="lg" fullWidth disabled={error !== null || saved} onClick={() => setSaved(true)} data-id="element-2768" data-mp="2qledwqh9vdw2">
          {saved && <CheckIcon size={15} aria-hidden="true" data-id="element-2769" data-mp="2lliaaschloh1" />}
          {t('ba.save')}
        </Button>
        {saved && <>
            <p role="status" className="rounded-card border border-sand-line bg-sand-deep/60 p-2 text-[12px] text-deep" data-id="element-2770" data-mp="2gorw6ak8t2y9">
              {t('ba.saved')}
            </p>
            <Button variant="outline" size="lg" fullWidth onClick={() => setConfirmShare(true)} data-id="element-2771" data-mp="evltkvpn3l6c">
              <Share2Icon size={15} aria-hidden="true" data-id="element-2772" data-mp="1cwq92g1957lq" />
              {t('ba.share')}
            </Button>
          </>}
        <p className="text-[11px] text-muted" data-id="element-2773" data-mp="17xwtmi2en6ix">{t('ba.prototypeNote')}</p>
      </div>

      <Modal open={confirmShare} onClose={() => setConfirmShare(false)} labelledBy="ba-share-title" data-id="element-2774" data-mp="1mue5rbif4p6j">
        <h2 id="ba-share-title" className="pr-5 text-section text-deep" data-id="element-2775" data-mp="1hxxggdzhg4rv">
          {t('ba.shareTitle')}
        </h2>
        <p className="mt-1 text-[13px] leading-relaxed text-muted" data-id="element-2776" data-mp="u7bacs91guki">{t('ba.shareBody')}</p>
        <div className="mt-3 space-y-2" data-id="element-2777" data-mp="p9i4k0bne2kr">
          <Button variant="primary" size="lg" fullWidth onClick={() => {
          setConfirmShare(false);
          setSharing(true);
        }} data-id="element-2778" data-mp="13wuu004kssgf">
            {t('ba.shareConfirm')}
          </Button>
          <Button variant="outline" size="lg" fullWidth onClick={() => setConfirmShare(false)} data-id="element-2779" data-mp="bepkqpoq608d">
            {t('ba.shareCancel')}
          </Button>
        </div>
      </Modal>

      <ShareSheet open={sharing} onClose={() => setSharing(false)} data-id="element-2780" data-mp="2i40z07zcvgf3" />
    </div>;
}